// Client-side checks for the Google Maps links admins paste into location fields.
//
// These must stay in step with Utils/mapLinkParser.js on the backend. The rule of thumb:
// accept anything the backend can parse, and warn (without blocking) when the link looks
// like it will only yield an approximate position.

// Any Google Maps host - google.com, google.es, google.com.pk, maps.google.com, ... -
// plus the shortened share links produced by "Share > Copy link" on mobile.
const MAPS_URL_RE =
  /^https?:\/\/(?:[\w.-]*\.)?google\.[a-z.]{2,6}\/maps\b|^https?:\/\/maps\.google\.[a-z.]{2,6}\/|^https?:\/\/(?:maps\.app\.goo\.gl|goo\.gl\/maps|g\.co\/kgs)\//i;

// A plain "lat, lng" pair typed by hand.
const BARE_PAIR_RE = /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/;

// The pin's true coordinates, as stored in the `data=` block.
const PLACE_COORDS_RE = /!3d-?\d+(\.\d+)?!4d-?\d+(\.\d+)?/;

// Shortened links carry nothing parseable until the backend follows the redirect.
const SHORT_LINK_RE = /^https?:\/\/(?:maps\.app\.goo\.gl|goo\.gl\/maps|g\.co\/kgs)\//i;

/** True when the backend will be able to make a location out of this value. */
export const isAcceptableMapLink = (value) => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  return MAPS_URL_RE.test(trimmed) || BARE_PAIR_RE.test(trimmed);
};

/**
 * Returns a warning string when the link will parse but only approximately, or null when
 * it carries exact pin coordinates. A link like `/maps/@31.46,74.27,13z` has no place
 * attached, so the backend can only fall back to the map's viewport centre - which is
 * wherever the map happened to be scrolled, not the place itself.
 */
export const mapLinkAccuracyWarning = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();

  if (!MAPS_URL_RE.test(trimmed)) return null;
  if (SHORT_LINK_RE.test(trimmed)) return null; // resolved server-side; can't tell yet
  if (PLACE_COORDS_RE.test(trimmed)) return null; // has exact pin coordinates

  return (
    "This link has no place attached, so the pin will be approximate. Search for the " +
    "place in Google Maps, click it, then use Share > Copy link."
  );
};

/** Ant Design validator shared by every location field. */
export const mapLinkValidator = (_, value) => {
  if (!value || isAcceptableMapLink(value)) return Promise.resolve();
  return Promise.reject(
    new Error(
      "Paste a Google Maps link for the place (Share > Copy link), or a 'latitude, longitude' pair."
    )
  );
};

/**
 * Validator for EDIT forms, which pre-fill the field with the saved address rather than
 * the original link. Leaving that untouched means "don't change the location", so it has
 * to pass validation as well.
 */
export const makeMapLinkEditValidator = (savedAddress) => (_, value) => {
  if (!value) return Promise.resolve();
  if (savedAddress && value.trim() === String(savedAddress).trim()) {
    return Promise.resolve();
  }
  return mapLinkValidator(_, value);
};

export const MAP_LINK_HINT =
  "Search the place in Google Maps, click the pin, then Share > Copy link and paste it here.";
